import type { ArtifactStore } from '@railgun-community/wallet'
import decompress from 'brotli/decompress'
import { Buffer } from 'buffer'
import { fetchVerifiedArtifact, getArtifactCacheVersion } from '@web/utils/railgunArtifactSources'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import leveljs from 'level-js'
import levelup from 'levelup'

/**
 * Hybrid artifact store for MV3 compliance.
 *
 *   - WASM (executable circuit code) + VKEY (verification JSON) are bundled
 *     into the extension package and served via `chrome.runtime.getURL`.
 *     These are loaded as `Uint8Array` / `string` directly — no network.
 *   - ZKEY (Groth16 proving key, ~600 MB total across 56 variants) is
 *     non-executable data. It is fetched lazily by the RAILGUN SDK from its
 *     public IPFS gateway at the moment a proof is generated, SHA-256-verified
 *     against the hash list bundled inside `@railgun-community/wallet`
 *     (`artifact-v2-hashes.json`), then cached in IndexedDB via this store.
 *
 * SDK download flow (see `services/railgun/core/artifacts.js`):
 *   1. `getArtifacts(variant)` is invoked from the prover.
 *   2. SDK tries `getDownloadedArtifacts` → calls `get(path)` for each of
 *      vkey / zkey / wasm. WASM + VKEY succeed from bundle; ZKEY misses on
 *      first use and the whole Promise.all rejects.
 *   3. SDK then constructs `ArtifactDownloader` and calls `downloadArtifacts`
 *      which, per artifact, runs `exists(path)`:
 *        - WASM / VKEY: exists() returns true (bundled), skip.
 *        - ZKEY: exists() returns false, axios GETs from IPFS, verifies the
 *          SHA-256, calls `store(dir, path, decompressedBytes)`.
 *   4. SDK re-reads via `get()` → returns wasm+vkey from bundle, zkey from
 *      IndexedDB cache → proof generation proceeds.
 *
 * The upstream SDK store layout (`artifactDownloadsPath`):
 *   non-POI: `artifacts-v2.1/{variant}/{zkey|wasm|vkey.json|dat}`
 *   POI:     `artifacts-v2.1/<poi-nov-2-23 | POI CID>/{variant}/{zkey|wasm|vkey.json|dat}`
 *
 * Bundled-file layout under `public/artifacts/`:
 *   `{variant}/wasm.br`
 *   `{variant}/vkey.json`
 *
 * See docs/railgun-artifacts-slim-bundle-plan.md.
 */

const ARTIFACTS_DIR_PREFIX = 'artifacts-v2.1/'
const ZKEY_DB_NAME = 'railgun-zkey-cache-v1'

type AssetKind = 'zkey' | 'wasm' | 'vkey'
type ResolvedAsset = {
  kind: AssetKind
  variant: string
  // For wasm/vkey only: where the file lives inside the extension bundle.
  bundledPath?: string
}

function resolveAsset(storePath: string): ResolvedAsset | null {
  if (!storePath.startsWith(ARTIFACTS_DIR_PREFIX)) return null
  // `artifacts-v2.1/<variant>/<file>`; POI files sit one level deeper, under
  // `poi-nov-2-23/` (SDK 10) or the POI artifact CID (SDK 11).
  const [tail, variant] = storePath.slice(ARTIFACTS_DIR_PREFIX.length).split('/').reverse()
  if (!tail || !variant) return null

  switch (tail) {
    case 'zkey':
      return { kind: 'zkey', variant }
    case 'wasm':
      return { kind: 'wasm', variant, bundledPath: `artifacts/${variant}/wasm.br` }
    case 'vkey.json':
      return { kind: 'vkey', variant, bundledPath: `artifacts/${variant}/vkey.json` }
    default:
      // `dat` (native prover) intentionally not shipped — browser uses WASM.
      return null
  }
}

function getBundledUrl(bundledPath: string): string {
  const runtime =
    typeof chrome !== 'undefined' && chrome.runtime && typeof chrome.runtime.getURL === 'function'
      ? chrome.runtime
      : undefined
  if (runtime) return runtime.getURL(bundledPath)
  return `/${bundledPath}`
}

async function fetchBundled(bundledPath: string): Promise<ArrayBuffer> {
  const url = getBundledUrl(bundledPath)
  const res = await fetch(url)
  if (!res.ok) {
    throw new Error(`bundled artifact missing: ${bundledPath} (HTTP ${res.status})`)
  }
  return res.arrayBuffer()
}

function brotliDecompressToBuffer(input: ArrayBuffer): Buffer {
  const compressed = Buffer.from(input)
  const out = decompress(compressed)
  if (!out) throw new Error('brotli decompress returned null')
  return Buffer.from(out)
}

const bundledMemCache = new Map<string, Buffer | string>()

type ZkeyDb = ReturnType<typeof levelup>
let zkeyDbInstance: ZkeyDb | null = null

function getZkeyDb(): ZkeyDb {
  if (zkeyDbInstance) return zkeyDbInstance
  zkeyDbInstance = levelup(leveljs(ZKEY_DB_NAME) as Parameters<typeof levelup>[0])
  return zkeyDbInstance
}

// The SDK reuses one store path per variant across releases; the suffix keeps a proving key
// cached by an older SDK from being served next to a newer circuit.
function zkeyCacheKey(storePath: string, variant: string): string {
  return `${storePath}@${getArtifactCacheVersion(variant)}`
}

export async function createBundledArtifactStore(): Promise<ArtifactStore> {
  const { ArtifactStore } = await loadRailgunSdk()
  const getFile = async (storePath: string): Promise<Buffer | string> => {
    const resolved = resolveAsset(storePath)
    if (!resolved) throw new Error(`unknown artifact path: ${storePath}`)

    if (resolved.kind === 'zkey') {
      const data = await getZkeyDb().get(zkeyCacheKey(storePath, resolved.variant))
      return Buffer.isBuffer(data) ? data : Buffer.from(data as ArrayBuffer)
    }

    const cached = bundledMemCache.get(storePath)
    if (cached !== undefined) return cached
    if (!resolved.bundledPath) throw new Error(`missing bundledPath: ${storePath}`)

    const raw = await fetchBundled(resolved.bundledPath)
    const value: Buffer | string =
      resolved.kind === 'vkey'
        ? Buffer.from(raw).toString('utf8')
        : brotliDecompressToBuffer(raw)
    bundledMemCache.set(storePath, value)
    return value
  }

  const storeFile = async (
    _dir: string,
    storePath: string,
    item: string | Uint8Array
  ): Promise<void> => {
    const resolved = resolveAsset(storePath)
    if (!resolved) return
    if (resolved.kind !== 'zkey') {
      // wasm/vkey are bundled, never overwritten.
      return
    }
    const data = typeof item === 'string' ? Buffer.from(item, 'utf8') : Buffer.from(item)
    await getZkeyDb().put(zkeyCacheKey(storePath, resolved.variant), data)
  }

  const fileExists = async (storePath: string): Promise<boolean> => {
    const resolved = resolveAsset(storePath)
    if (!resolved) return false

    if (resolved.kind === 'zkey') {
      const key = zkeyCacheKey(storePath, resolved.variant)
      try {
        await getZkeyDb().get(key)
        return true
      } catch {
        // Not cached: fetch it ourselves so a dead SDK gateway has fallbacks. On null the SDK
        // still tries its own gateway and reports the failure.
        const data = await fetchVerifiedArtifact(resolved.variant, 'zkey', (msg) =>
          console.warn(`[RailgunArtifacts] ${msg}`)
        )
        if (!data) return false
        await getZkeyDb().put(key, data)
        return true
      }
    }

    if (bundledMemCache.has(storePath)) return true
    if (!resolved.bundledPath) return false
    try {
      const res = await fetch(getBundledUrl(resolved.bundledPath), { method: 'HEAD' })
      return res.ok
    } catch {
      return false
    }
  }

  return new ArtifactStore(getFile as never, storeFile as never, fileExists)
}
